
import { app } from "@/config/globals";
import {
  computed,
  defineComponent,
  inject,
  ref,
  watchEffect,
  onUnmounted,
} from "vue";
import {
  loadStripe,
  Stripe,
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeCardCvcElement,
  StripeElementStyle,
} from "@stripe/stripe-js";
import { showMessage } from "@/services/MessageService";
import i18n from "@/i18n";
import { useStore } from "vuex";
import { functions } from "@/config/firebase";
import { useRoute, useRouter } from "vue-router";
import { loaderStart, loaderDone } from "@/services/Loader";

export default defineComponent({
  setup(props) {
    const cardAlertMessage = ref("");
    let stripe: Stripe | null = {} as Stripe;
    let cardNumber: StripeCardNumberElement = {} as StripeCardNumberElement;
    let cardExpiry: StripeCardExpiryElement = {} as StripeCardExpiryElement;
    let cardCvc: StripeCardCvcElement = {} as StripeCardCvcElement;
    const emitter: any = inject("emitter");
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const participantData = computed(() => {
      return store.getters["user/participantData"];
    });
    const eventData = computed(() => {
      return store.getters["user/eventData"];
    });
    const resetCart = () => {
      emitter.emit("resetCart");
    };
    const onFormSubmit = async () => {
      loaderStart();
      try {
        const registerCourse = functions.httpsCallable("registerCourse");
        const checkQuotaLimit = await registerCourse({
          eventID: eventData.value.id,
          participantID: participantData.value.uid,
          courseID: props.courseID,
        });
        if (
          props.stripeClientSecret &&
          stripe &&
          checkQuotaLimit.data === "courseWithPrice"
        ) {
          if (props.couponApply) {
            const stripeUpdateCouponRedemptions = functions.httpsCallable(
              "stripeUpdateCouponRedemptions"
            );
            const validRedemptions = await stripeUpdateCouponRedemptions({
              coupongId: props.couponId,
              eventId: route.params.eventID,
              coupongType: "course",
            });
            if (validRedemptions.data === "valid") {
              const result = await stripe.confirmCardPayment(
                props.stripeClientSecret,
                {
                  payment_method: {
                    card: cardNumber,
                    billing_details: {
                      name: participantData.value.fullName
                        ? participantData.value.fullName
                        : participantData.value.firstName,
                    },
                  },
                }
              );
              if (result.error) {
                showMessage({
                  message: translateErrorCode(result.error.code),
                  type: "error",
                  title: "",
                });
              } else {
                // The payment has been processed!
                if (result.paymentIntent.status === "succeeded") {
                  showMessage({
                    title: i18n.global.t("register.paymentSuccess.title"),
                    message: i18n.global.t("register.paymentSuccess.message"),
                    type: "success",
                  });
                  router.push({
                    name: "Courses",
                    params: { eventID: eventData.value.id },
                  });
                  return;
                }
              }
            } else {
              showMessage({
                title: i18n.global.t("coupon.invalidCoupon"),
                message: i18n.global.t("coupon.invalidCouponDetail"),
                type: "warning",
              });
              resetCart();
              return;
            }
          }
          const result = await stripe.confirmCardPayment(
            props.stripeClientSecret,
            {
              payment_method: {
                card: cardNumber,
                billing_details: {
                  name: participantData.value.firstName,
                },
              },
            }
          );
          if (result.error) {
            showMessage({
              message: translateErrorCode(result.error.code),
              type: "error",
              title: "",
            });
          } else {
            // The payment has been processed!
            if (result.paymentIntent.status === "succeeded") {
              showMessage({
                title: i18n.global.t("register.paymentSuccess.title"),
                message: i18n.global.t("register.paymentSuccess.message"),
                type: "success",
              });
              showMessage({
                title: i18n.global.t(
                  "program.registerCourse.registerSuccess.title"
                ),
                message: i18n.global.t(
                  "program.registerCourse.registerSuccess.message"
                ),
                type: "success",
              });
              router.push({
                name: "Courses",
                params: { eventID: eventData.value.id },
              });
            }
          }
        } else if (checkQuotaLimit.data === "soldOut") {
          showMessage({
            title: i18n.global.t("program.registerCourse.soldOutError.title"),
            message: i18n.global.t(
              "program.registerCourse.soldOutError.message"
            ),
            type: "error",
          });
          router.push({
            name: "Program",
            params: { eventID: eventData.value.id },
          });
        }
      } catch {
        showMessage({
          title: i18n.global.t("program.registerCourse.registerError.title"),
          message: i18n.global.t(
            "program.registerCourse.registerError.message"
          ),
          type: "error",
        });
        router.push({
          name: "Program",
          params: { eventID: eventData.value.id },
        });
      } finally {
        loaderDone();
      }
    };
    async function createStripeInstance(connectedAccount: string) {
      if (app.api.stripe.publicKey) {
        let getStripe = await loadStripe(app.api.stripe.publicKey, {
          stripeAccount: connectedAccount,
        });
        if (stripe != null) {
          stripe = getStripe;
          const elements = stripe?.elements();
          if (elements) {
            const styleFields = {
              base: {
                color: "black",
                fontFamily: '"Roboto", Helvetica, sans-serif',
                fontSmoothing: "antialiased",
                fontSize: "14px",
                "::placeholder": {
                  color: "#aab7c4",
                },
                borderBottomColor: "black",
              },
              invalid: {
                color: "#fa755a",
                iconColor: "#fa755a",
              },
            } as StripeElementStyle;
            cardNumber = elements?.create("cardNumber", {
              style: styleFields,
              placeholder: i18n.global.t("register.payment.cardNumber"),
            });
            cardNumber.mount("#card-number");
            cardNumber.on("change", ({ error }) => {
              if (error) {
                cardAlertMessage.value = translateErrorCode(error.code);
              } else {
                cardAlertMessage.value = "";
              }
            });
            cardExpiry = elements?.create("cardExpiry", {
              style: styleFields,
              placeholder: i18n.global.t("register.payment.expirationDate"),
            });
            cardExpiry.mount("#card-expiry");
            cardExpiry.on("change", ({ error }) => {
              if (error) {
                cardAlertMessage.value = translateErrorCode(error.code);
              } else {
                cardAlertMessage.value = "";
              }
            });
            cardCvc = elements?.create("cardCvc", {
              style: styleFields,
              placeholder: "CVC",
            });
            cardCvc.mount("#card-cvc");
            cardCvc.on("change", ({ error }) => {
              if (error) {
                cardAlertMessage.value = translateErrorCode(error.code);
              } else {
                cardAlertMessage.value = "";
              }
            });
          }
        }
      }
    }
    function translateErrorCode(errorCode): string {
      switch (errorCode) {
        case "incomplete_number":
          return i18n.global.t(
            "register.payment.errorMessages.incompleteNumber"
          );
        case "invalid_number":
          return i18n.global.t("register.payment.errorMessages.invalidNumber");
        case "incomplete_expiry":
          return i18n.global.t(
            "register.payment.errorMessages.incomplateExpiry"
          );
        case "invalid_expiry_year_past":
          return i18n.global.t("register.payment.errorMessages.invalidExpiry");
        case "incomplete_cvc":
          return i18n.global.t("register.payment.errorMessages.incompleteCvc");
        default:
          return "Error";
      }
    }
    onUnmounted(() => {
      cardNumber.destroy();
      cardExpiry.destroy();
      cardCvc.destroy();
    });
    watchEffect(() => {
      if (props.stripeAccountId) createStripeInstance(props.stripeAccountId);
    });
    return {
      cardAlertMessage,
      stripe,
      onFormSubmit,
      resetCart,
    };
  },
  props: {
    stripeClientSecret: String,
    stripeAccountId: String,
    couponId: String,
    couponApply: Boolean,
    courseID: String,
  },
});
